import { ArrowBackIos } from '@mui/icons-material';
import { DialogActions, DialogContent, DialogTitle, Dialog as MuiDialog } from '@mui/material';
import { DataTable, Loader } from 'components';
import { DataTableColumn } from 'components/tables/data-table';
import dayjs from 'dayjs';
import { FC, useCallback, useState } from 'react';
import { deleteInvoiceItemBillableEvent } from 'services';
import { BillableEvent, BillableEventType } from 'types';
import { BillableEventView, EditAction } from '../enums';

import './billable-events-list.scss';

export const BillableEventList: FC<{
  invoiceId: number;
  billableEvents: BillableEvent[];
  setSelectedAction: (action: EditAction | null) => void;
  close: () => void;
  onDelete: () => void;
  setView: (view: BillableEventView) => void;
  setBillableEvent: (event: BillableEvent | undefined) => void;
}> = ({ billableEvents, close, setSelectedAction, setView, setBillableEvent, onDelete, invoiceId }) => {
  const [eventToDelete, setEventToDelete] = useState<BillableEvent | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  const columns: DataTableColumn[] = [
    {
      field: 'timestamp',
      label: 'Date',
      type: 'date',
      width: 140,
      getValue: (row: BillableEvent) => (!row.timestamp ? null : new Date(row.timestamp)),
      getValueLabel: (value: Date) => (!value ? null : dayjs(value).utc().format('M/D/YYYY'))
    },
    {
      field: 'type',
      label: 'Type',
      getValueLabel: (value: BillableEventType) => {
        switch (value) {
          case BillableEventType.LIFT_OFF:
            return 'Lift Off';
          case BillableEventType.LIFT_ON:
            return 'Lift On';
          case BillableEventType.DIG:
            return 'Dig';
          case BillableEventType.CREDIT:
            return 'Credit';
          case BillableEventType.OTHER:
          default:
            return 'Other';
        }
      },
      width: 100
    },
    {
      field: 'label',
      label: 'Notes',
      width: 150
    },
    {
      field: 'unitNumber',
      label: 'Unit',
      width: 200
    },
    {
      field: 'cost',
      label: 'Cost',
      type: 'number',
      width: 125,
      getValueLabel: (value: number) => value.toFixed(2)
    },
    {
      field: 'operator',
      label: 'Operator',
      width: 150
    }
  ];

  const getBillableEvents = useCallback(async () => {
    return billableEvents;
  }, [billableEvents]);

  const doDelete = useCallback(() => {
    if (!eventToDelete) {
      return;
    }
    setDeleteLoading(true);
    deleteInvoiceItemBillableEvent({ invoiceId, billableEventId: eventToDelete.id })
      .then(() => {
        close();
        onDelete();
      })
      .finally(() => setDeleteLoading(false));
  }, [close, eventToDelete, invoiceId, onDelete]);

  return (
    <div style={{ minWidth: '1px' }}>
      <MuiDialog open={showDeleteConfirmation}>
        <DialogTitle>Delete Billable Event?</DialogTitle>
        <DialogContent>
          <p>Are you sure you want to delete this event?</p>
        </DialogContent>
        <DialogActions>
          <div className="d-flex g-2">
            <button
              className="btn-outline"
              onClick={() => {
                setEventToDelete(null);
                setShowDeleteConfirmation(false);
              }}
            >
              Cancel
            </button>
            <Loader loading={deleteLoading} scale="50%">
              <button
                className="btn-danger"
                onClick={() => {
                  doDelete();
                }}
              >
                Delete
              </button>
            </Loader>
          </div>
        </DialogActions>
      </MuiDialog>

      <DataTable
        tableClass="billable-events-list"
        fetchData={getBillableEvents}
        columns={columns}
        initialColumns={columns.map(col => {
          return { field: col.field, width: col.width };
        })}
        initialPage={{ sortField: 'timestamp', sortDirection: 'desc' }}
        getRowActions={(row: BillableEvent) => {
          return [
            <button
              className="btn-close grid-1"
              onClick={() => {
                setBillableEvent(row);
                setView(BillableEventView.FORM);
              }}
            >
              <span className="material-icons">edit</span>
            </button>,
            <button
              className="btn-close grid-1"
              onClick={() => {
                setEventToDelete(row);
                setShowDeleteConfirmation(true);
              }}
            >
              <span className="material-icons">delete</span>
            </button>
          ];
        }}
        clientSidePagination
        csvFilename="Billable Events"
      />
      <div className="d-flex align-items-center justify-content-between mt-3">
        <div>
          <button className="btn-back" onClick={() => setSelectedAction(null)}>
            <ArrowBackIos fontSize="small" />
            Back
          </button>
        </div>
        <div className="d-flex g-2">
          <button type="button" className="btn-outline" onClick={() => close()}>
            Cancel
          </button>
          <button type="button" className="btn-primary" onClick={() => setView(BillableEventView.FORM)}>
            Add Billable Event
          </button>
        </div>
      </div>
    </div>
  );
};
